import { useState, useEffect, type JSX, type ReactNode } from 'react';
import {
  TrendingUp,
  Package,
  ShoppingCart,
  Truck,
  RotateCcw,
  ExternalLink,
  DollarSign,
  Sliders,
  BarChart3,
  Download,
  type LucideIcon,
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import { useParamStore } from '../../hooks/useParamStore';
import ArbiSelect from '../../components/ui/ArbiSelect';

// ── Local types ─────────────────────────────────────────────────────────────

type DateRangeId = '7d' | '30d' | '90d' | 'month' | 'last_month';
type ReportId =
  | 'profitability'
  | 'inventory'
  | 'purchasing'
  | 'operations'
  | 'returns'
  | 'listings'
  | 'finance'
  | 'report-builder'
  | 'analytics';

interface DateRange {
  start: string;
  end: string;
}

interface ChartTooltipPayloadEntry {
  name?: string;
  value?: number | string;
  color?: string;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: ChartTooltipPayloadEntry[];
  label?: string | number;
}

interface RevenueDatum {
  date: string;
  revenue: number;
}

interface NamedValueDatum {
  name: string;
  value: number;
}

interface TopProductRow {
  isg_sku: string;
  title: string;
  units: number;
  revenue: number;
  channel?: string | null;
}

interface CreditSummaryShape {
  utilization_pct?: number | null;
}

type GenericSupabaseRpc = (
  name: string,
  args?: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as GenericSupabaseRpc;

// ── Formatting ──────────────────────────────────────────────────────────────

function fmtCurrency(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '\u2014';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtPct(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '\u2014';
  return `${Number(v).toFixed(1)}%`;
}

function fmtCount(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '\u2014';
  return Number(v).toLocaleString();
}

// ── Date ranges ─────────────────────────────────────────────────────────────

const DATE_RANGES: ReadonlyArray<{ value: DateRangeId; label: string }> = [
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
  { value: 'month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
] as const;

function getDateRange(rangeId: DateRangeId): DateRange {
  const now = new Date();
  const end = now.toISOString();
  let start: string;
  switch (rangeId) {
    case '7d':
      start = new Date(now.getTime() - 7 * 86400000).toISOString();
      break;
    case '30d':
      start = new Date(now.getTime() - 30 * 86400000).toISOString();
      break;
    case '90d':
      start = new Date(now.getTime() - 90 * 86400000).toISOString();
      break;
    case 'month':
      start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      break;
    case 'last_month': {
      const m = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start = m.toISOString();
      break;
    }
    default:
      start = new Date(now.getTime() - 30 * 86400000).toISOString();
  }
  return { start, end };
}

// ── Shared chart tooltip ────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }: ChartTooltipProps): JSX.Element | null {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-3 border border-border rounded-md px-3 py-2 shadow-lg text-xs">
      <p className="text-ink-muted mb-1 font-mono">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-mono">
          {p.name}: {typeof p.value === 'number' && p.value > 100 ? fmtCurrency(p.value) : p.value}
        </p>
      ))}
    </div>
  );
}

// ── Metric card ─────────────────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: string | number;
  color?: string;
}

function MetricCard({ label, value, color = 'text-ink-bright' }: MetricCardProps): JSX.Element {
  return (
    <div className="bg-surface-1 border border-border rounded-lg p-4">
      <p className="text-2xs text-ink-muted uppercase tracking-wider mb-1">{label}</p>
      <p className={clsx('text-lg font-semibold font-mono', color)}>{value}</p>
    </div>
  );
}

// ── Chart card wrapper ──────────────────────────────────────────────────────

interface ChartCardProps {
  title: string;
  children?: ReactNode;
  empty?: boolean;
}

function ChartCard({ title, children, empty }: ChartCardProps): JSX.Element {
  return (
    <div className="bg-surface-1 border border-border rounded-lg p-4">
      <h3 className="text-sm font-semibold text-ink-bright mb-3">{title}</h3>
      {empty ? (
        <div className="h-48 flex items-center justify-center">
          <p className="text-xs text-ink-muted">No data yet</p>
        </div>
      ) : (
        <div className="h-48">{children}</div>
      )}
    </div>
  );
}

// ── Report header ───────────────────────────────────────────────────────────

interface ReportHeaderProps {
  title: string;
  range?: DateRangeId;
  onRangeChange?: (v: DateRangeId) => void;
  onExport?: () => void;
}

function ReportHeader({ title, range, onRangeChange, onExport }: ReportHeaderProps): JSX.Element {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-semibold text-ink-bright">{title}</h2>
      <div className="flex items-center gap-2">
        {onRangeChange && (
          <ArbiSelect
            value={range ?? '30d'}
            options={[...DATE_RANGES]}
            onChange={(v) => onRangeChange(String(v) as DateRangeId)}
            className="w-40"
          />
        )}
        {onExport && (
          <button
            onClick={onExport}
            className="arbi-btn-ghost text-xs px-2 py-1 flex items-center gap-1"
          >
            <Download size={12} /> Export CSV
          </button>
        )}
      </div>
    </div>
  );
}

// ── Chart colors ────────────────────────────────────────────────────────────

const COLORS = {
  info: 'var(--info)',
  success: 'var(--success)',
  warning: 'var(--warning)',
  danger: 'var(--danger)',
  accent: 'var(--accent)',
  muted: 'var(--ink-muted)',
} as const;
const PIE_COLORS: readonly string[] = [
  COLORS.info,
  COLORS.success,
  COLORS.warning,
  COLORS.danger,
  COLORS.accent,
  COLORS.muted,
];

// ============================================================================
// PROFITABILITY REPORT
// ============================================================================

interface ProfitabilityData {
  revenue: number;
  profit: number;
  avgRoi: number;
  unitsSold: number;
  topProducts: TopProductRow[];
  chartData: RevenueDatum[];
}

interface FulfillmentOrderLite {
  quantity: number | null;
  unit_price: number | null;
  channel: string | null;
  product_id: string | null;
  products: { isg_sku: string | null; title: string | null } | null;
  created_at: string | null;
}

function ProfitabilityReport(): JSX.Element {
  const [range, setRange] = useState<DateRangeId>('30d');
  const [data, setData] = useState<ProfitabilityData>({
    revenue: 0,
    profit: 0,
    avgRoi: 0,
    unitsSold: 0,
    topProducts: [],
    chartData: [],
  });

  useEffect(() => {
    const { start, end } = getDateRange(range);
    // Fetch fulfilled orders
    supabase
      .from('fulfillment_orders')
      .select('quantity, unit_price, channel, product_id, products(isg_sku, title), created_at')
      .in('status', ['delivered', 'channel_fulfilled'])
      .gte('created_at', start)
      .lte('created_at', end)
      .order('created_at', { ascending: true })
      .limit(500)
      .then(
        ({ data: orders }) => {
          const rows = (orders as unknown as FulfillmentOrderLite[]) || [];
          const revenue = rows.reduce((s, o) => s + (o.quantity || 0) * (o.unit_price || 0), 0);
          const unitsSold = rows.reduce((s, o) => s + (o.quantity || 0), 0);

          // Group by product for top products
          const byProduct: Record<string, TopProductRow> = {};
          for (const o of rows) {
            const sku = o.products?.isg_sku || 'Unknown';
            if (!byProduct[sku])
              byProduct[sku] = {
                isg_sku: sku,
                title: o.products?.title || '',
                units: 0,
                revenue: 0,
                channel: o.channel,
              };
            byProduct[sku].units += o.quantity || 0;
            byProduct[sku].revenue += (o.quantity || 0) * (o.unit_price || 0);
          }
          const topProducts = Object.values(byProduct)
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, 10);

          // Group by date for chart
          const byDate: Record<string, RevenueDatum> = {};
          for (const o of rows) {
            if (!o.created_at) continue;
            const d = new Date(o.created_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            });
            if (!byDate[d]) byDate[d] = { date: d, revenue: 0 };
            byDate[d].revenue += (o.quantity || 0) * (o.unit_price || 0);
          }

          // FN-4 R5-F1: profit/ROI are sourced from the ENGINE (product_profitability via
          // the server RPC), NOT the old client-side `revenue * 0.25` fabrication
          // (Architecture Rule 6). They are engine ESTIMATES (current cost basis — V1
          // captures no realized cost), surfaced as "Est." with the caption below.
          callRpc('get_profitability_report_summary', { p_start: start, p_end: end }).then(
            ({ data: prof }) => {
              const p = (prof as { est_profit?: number; est_roi_pct?: number } | null) ?? {};
              setData({
                revenue,
                profit: Number(p.est_profit) || 0,
                avgRoi: Number(p.est_roi_pct) || 0,
                unitsSold,
                topProducts,
                chartData: Object.values(byDate),
              });
            },
            (e: unknown) => console.error('[Reports] profitability summary RPC error:', e)
          );
        },
        (e: unknown) => console.error('[Reports] profitability fulfilled orders error:', e)
      );
  }, [range]);

  return (
    <div className="space-y-6">
      <ReportHeader
        title="Profitability Report"
        range={range}
        onRangeChange={setRange}
        onExport={() => {}}
      />
      <div className="grid grid-cols-4 gap-4">
        <MetricCard label="Total Revenue" value={fmtCurrency(data.revenue)} color="text-info" />
        <MetricCard label="Est. Profit" value={fmtCurrency(data.profit)} color="text-success" />
        <MetricCard label="Avg ROI%" value={fmtPct(data.avgRoi)} />
        <MetricCard label="Units Sold" value={fmtCount(data.unitsSold)} />
      </div>
      {/* FN-4 R5-F1: profit/ROI are engine estimates (current cost basis), not realized cost. */}
      <p className="text-2xs text-ink-muted -mt-3">
        Est. Profit &amp; ROI use the engine&rsquo;s current cost estimates applied to sold units,
        not realized cost. Revenue and units are actuals.
      </p>
      <ChartCard title="Revenue Trend" empty={data.chartData.length === 0}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--ink-muted)' }} />
            <YAxis tick={{ fontSize: 10, fill: 'var(--ink-muted)' }} />
            <Tooltip content={<ChartTooltip />} />
            <Line
              type="monotone"
              dataKey="revenue"
              name="Revenue"
              stroke={COLORS.info}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
      {data.topProducts.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-ink-bright mb-2">Top Products</h3>
          <div className="bg-surface-1 border border-border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border text-2xs text-ink-muted uppercase tracking-wider">
                  <th className="text-left px-3 py-2">ISG SKU</th>
                  <th className="text-left px-3 py-2">Title</th>
                  <th className="text-right px-3 py-2">Units</th>
                  <th className="text-right px-3 py-2">Revenue</th>
                  <th className="text-left px-3 py-2">Channel</th>
                </tr>
              </thead>
              <tbody>
                {data.topProducts.map((p, i) => (
                  <tr
                    key={i}
                    className="border-b border-border-subtle hover:bg-surface-3 transition-colors"
                  >
                    <td className="px-3 py-2 font-mono text-accent">{p.isg_sku}</td>
                    <td className="px-3 py-2 text-ink-primary truncate max-w-[200px]">{p.title}</td>
                    <td className="px-3 py-2 font-mono text-right">{p.units}</td>
                    <td className="px-3 py-2 font-mono text-right">{fmtCurrency(p.revenue)}</td>
                    <td className="px-3 py-2">{p.channel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PURCHASING REPORT
// ============================================================================

interface POBrief {
  id: string;
  po_number: string | null;
  status: string | null;
  total_cost: number | null;
  total_skus: number | null;
  created_at: string | null;
  distributors?: { name: string | null } | null;
}

interface PurchasingData {
  totalPOs: number;
  totalSpend: number;
  utilPct: number;
  avgValue: number;
  chartData: Array<{ date: string; count: number }>;
  poList: POBrief[];
}

function PurchasingReport(): JSX.Element {
  const { activeDistributor } = useApp();
  const paramStore = useParamStore();
  const dangerThreshold = Number(
    paramStore.resolveKey('credit_util_danger_pct')?.param.value ?? 80
  );
  const [range, setRange] = useState<DateRangeId>('30d');
  const [data, setData] = useState<PurchasingData>({
    totalPOs: 0,
    totalSpend: 0,
    utilPct: 0,
    avgValue: 0,
    chartData: [],
    poList: [],
  });

  useEffect(() => {
    const { start } = getDateRange(range);
    Promise.all([
      supabase
        .from('purchase_orders')
        .select('id, po_number, status, total_cost, total_skus, created_at, distributors(name)')
        .gte('created_at', start)
        .order('created_at', { ascending: false })
        .limit(200),
      activeDistributor?.id
        ? callRpc('get_credit_summary', { p_distributor_id: activeDistributor.id })
        : Promise.resolve({ data: null, error: null }),
    ])
      .then(([posRes, creditRes]) => {
        const pos = (posRes.data as unknown as POBrief[]) || [];
        const totalPOs = pos.length;
        const totalSpend = pos
          .filter((p) => p.status === 'received')
          .reduce((s, p) => s + (p.total_cost || 0), 0);
        const avgValue = totalPOs > 0 ? totalSpend / totalPOs : 0;
        const creditData = creditRes.data as CreditSummaryShape | null;
        const utilPct = creditData?.utilization_pct || 0;

        // Group by week for chart
        const byWeek: Record<string, { date: string; count: number }> = {};
        for (const p of pos) {
          if (!p.created_at) continue;
          const w = new Date(p.created_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          });
          if (!byWeek[w]) byWeek[w] = { date: w, count: 0 };
          byWeek[w].count++;
        }

        setData({
          totalPOs,
          totalSpend,
          utilPct,
          avgValue,
          chartData: Object.values(byWeek).reverse(),
          poList: pos.slice(0, 20),
        });
      })
      .catch((e) => console.error('[Reports] purchasing POs and credit error:', e));
  }, [range, activeDistributor]);

  return (
    <div className="space-y-6">
      <ReportHeader
        title="Purchasing Report"
        range={range}
        onRangeChange={setRange}
        onExport={() => {}}
      />
      <div className="grid grid-cols-4 gap-4">
        <MetricCard label="Total POs" value={fmtCount(data.totalPOs)} />
        <MetricCard label="Total Spend" value={fmtCurrency(data.totalSpend)} color="text-danger" />
        <MetricCard
          label="Credit Utilization"
          value={fmtPct(data.utilPct)}
          color={data.utilPct > dangerThreshold ? 'text-danger' : 'text-info'}
        />
        <MetricCard label="Avg PO Value" value={fmtCurrency(data.avgValue)} />
      </div>
      <ChartCard title="PO Volume" empty={data.chartData.length === 0}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--ink-muted)' }} />
            <YAxis tick={{ fontSize: 10, fill: 'var(--ink-muted)' }} allowDecimals={false} />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="count" name="POs" fill={COLORS.info} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

// ============================================================================
// INVENTORY REPORT
// ============================================================================

interface InventoryData {
  total: number;
  warehouse: number;
  fbaLive: number;
  outOfStock: number;
  byLocation: NamedValueDatum[];
}

function InventoryReport(): JSX.Element {
  const [data, setData] = useState<InventoryData>({
    total: 0,
    warehouse: 0,
    fbaLive: 0,
    outOfStock: 0,
    byLocation: [],
  });

  useEffect(() => {
    callRpc('get_inventory_counts')
      .then(({ data: counts }) => {
        if (!counts || typeof counts !== 'object') return;
        const c = counts as Record<string, number | null | undefined>;
        const total = Object.values(c).reduce((s: number, v) => s + (v || 0), 0);
        const byLocation = Object.entries(c)
          .map(([k, v]) => ({ name: k.replace(/_/g, ' '), value: v || 0 }))
          .filter((l) => l.value > 0);
        setData({
          total,
          warehouse: c.at_warehouse || 0,
          fbaLive: c.fba_live || 0,
          outOfStock: c.out_of_stock || 0,
          byLocation,
        });
      })
      .then(undefined, (e: unknown) => console.error('[Reports] inventory counts error:', e));
  }, []);

  return (
    <div className="space-y-6">
      <ReportHeader title="Inventory Report" onExport={() => {}} />
      <div className="grid grid-cols-4 gap-4">
        <MetricCard label="Total Units" value={fmtCount(data.total)} />
        <MetricCard label="At Warehouse" value={fmtCount(data.warehouse)} color="text-info" />
        <MetricCard label="FBA Live" value={fmtCount(data.fbaLive)} color="text-success" />
        <MetricCard label="Out of Stock" value={fmtCount(data.outOfStock)} color="text-danger" />
      </div>
      <ChartCard title="Inventory by Location" empty={data.byLocation.length === 0}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.byLocation} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
            <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--ink-muted)' }} />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 10, fill: 'var(--ink-muted)' }}
              width={100}
            />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="value" name="Units" fill={COLORS.info} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

// ============================================================================
// OPERATIONS REPORT
// ============================================================================

interface OperationsData {
  fulfilled: number;
  avgDays: number;
  onHold: number;
  discrepancyRate: number;
}

function OperationsReport(): JSX.Element {
  const [range, setRange] = useState<DateRangeId>('30d');
  const [data, setData] = useState<OperationsData>({
    fulfilled: 0,
    avgDays: 0,
    onHold: 0,
    discrepancyRate: 0,
  });

  useEffect(() => {
    // FN-4 R5-F1 sibling: avgDays was a hardcoded 3.2. Real-source it from the
    // actual fulfillment duration (shipped_at − created_at) over completed orders —
    // a date diff, not profitability math (Rule 6 unaffected).
    Promise.all([
      callRpc('get_fulfillment_counts'),
      supabase
        .from('fulfillment_orders')
        .select('created_at, shipped_at')
        .in('status', ['delivered', 'channel_fulfilled'])
        .not('shipped_at', 'is', null)
        .limit(1000),
    ])
      .then(([countsRes, durRes]) => {
        const counts = countsRes.data;
        if (!counts || typeof counts !== 'object') return;
        const c = counts as Record<string, number | null | undefined>;
        const fulfilled = (c.delivered || 0) + (c.channel_fulfilled || 0);
        const onHold = c.on_hold || 0;
        const discrepancy = c.discrepancy || 0;
        const total = Object.values(c).reduce((s: number, v) => s + (v || 0), 0);
        const durRows = (durRes.data as { created_at: string; shipped_at: string }[] | null) ?? [];
        const days = durRows
          .map(
            (o) =>
              (new Date(o.shipped_at).getTime() - new Date(o.created_at).getTime()) / 86_400_000
          )
          .filter((d) => d >= 0);
        const avgDays = days.length ? days.reduce((s, d) => s + d, 0) / days.length : 0;
        setData({
          fulfilled,
          avgDays: Math.round(avgDays * 10) / 10,
          onHold,
          discrepancyRate: total > 0 ? (discrepancy / total) * 100 : 0,
        });
      })
      .then(undefined, (e: unknown) =>
        console.error('[Reports] operations fulfillment metrics error:', e)
      );
    void range;
  }, [range]);

  return (
    <div className="space-y-6">
      <ReportHeader
        title="Operations Report"
        range={range}
        onRangeChange={setRange}
        onExport={() => {}}
      />
      <div className="grid grid-cols-4 gap-4">
        <MetricCard
          label="Orders Fulfilled"
          value={fmtCount(data.fulfilled)}
          color="text-success"
        />
        <MetricCard
          label="Avg Fulfillment Time"
          value={data.avgDays > 0 ? `${data.avgDays}d` : '—'}
        />
        <MetricCard label="On Hold" value={fmtCount(data.onHold)} color="text-warning" />
        <MetricCard
          label="Discrepancy Rate"
          value={fmtPct(data.discrepancyRate)}
          color={data.discrepancyRate > 5 ? 'text-danger' : 'text-ink-bright'}
        />
      </div>
    </div>
  );
}

// ============================================================================
// RETURNS REPORT
// ============================================================================

interface ReturnsData {
  total: number;
  returnRate: number;
  dispositions: NamedValueDatum[];
}

interface ReturnBrief {
  id: string;
  disposition: string | null;
  status: string | null;
  created_at: string | null;
}

function ReturnsReport(): JSX.Element {
  const [range, setRange] = useState<DateRangeId>('30d');
  const [data, setData] = useState<ReturnsData>({ total: 0, returnRate: 0, dispositions: [] });

  useEffect(() => {
    const { start } = getDateRange(range);
    supabase
      .from('returns')
      .select('id, disposition, status, created_at')
      .gte('created_at', start)
      .limit(500)
      .then(
        ({ data: returns }) => {
          const rows = (returns as unknown as ReturnBrief[]) || [];
          const total = rows.length;
          const byDisp: Record<string, number> = {};
          for (const r of rows) {
            const d = r.disposition || 'pending';
            byDisp[d] = (byDisp[d] || 0) + 1;
          }
          setData({
            total,
            returnRate: 0,
            dispositions: Object.entries(byDisp).map(([name, value]) => ({ name, value })),
          });
        },
        (e: unknown) => console.error('[Reports] returns data error:', e)
      );
  }, [range]);

  return (
    <div className="space-y-6">
      <ReportHeader
        title="Returns Report"
        range={range}
        onRangeChange={setRange}
        onExport={() => {}}
      />
      <div className="grid grid-cols-3 gap-4">
        <MetricCard label="Total Returns" value={fmtCount(data.total)} color="text-warning" />
        <MetricCard label="Return Rate" value={fmtPct(data.returnRate)} />
        <MetricCard label="Dispositions" value={fmtCount(data.dispositions.length)} />
      </div>
      <ChartCard title="Disposition Breakdown" empty={data.dispositions.length === 0}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data.dispositions}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={70}
              label={({ name, value }) => `${name} (${value})`}
            >
              {data.dispositions.map((_, i) => (
                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

// ============================================================================
// LISTINGS REPORT
// ============================================================================

interface ListingsData {
  active: number;
  suppressed: number;
  pending: number;
}

function ListingsReport(): JSX.Element {
  const [data, setData] = useState<ListingsData>({ active: 0, suppressed: 0, pending: 0 });

  useEffect(() => {
    Promise.all([
      supabase
        .from('listing_links')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'active'),
      supabase
        .from('listing_links')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'suppressed'),
      supabase
        .from('listing_links')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending'),
    ])
      .then(([a, s, p]) => {
        setData({ active: a.count || 0, suppressed: s.count || 0, pending: p.count || 0 });
      })
      .catch((e: unknown) => console.error('[Reports] listings counts error:', e));
  }, []);

  return (
    <div className="space-y-6">
      <ReportHeader title="Listings Report" onExport={() => {}} />
      <div className="grid grid-cols-3 gap-4">
        <MetricCard label="Active Listings" value={fmtCount(data.active)} color="text-success" />
        <MetricCard label="Suppressed" value={fmtCount(data.suppressed)} color="text-danger" />
        <MetricCard label="Pending" value={fmtCount(data.pending)} color="text-warning" />
      </div>
    </div>
  );
}

// ============================================================================
// FINANCE REPORT
// ============================================================================

interface FinanceReportData {
  invoiced: number;
  paid: number;
  outstanding: number;
  payoutsReceived: number;
}

interface InvoiceBrief {
  amount: number | null;
  status: string | null;
}

interface PayoutBrief {
  net_amount: number | null;
  status: string | null;
}

function FinanceReport(): JSX.Element {
  const [data, setData] = useState<FinanceReportData>({
    invoiced: 0,
    paid: 0,
    outstanding: 0,
    payoutsReceived: 0,
  });

  useEffect(() => {
    Promise.all([
      supabase.from('invoices').select('amount, status'),
      supabase.from('payouts').select('net_amount, status'),
    ])
      .then(([invRes, payRes]) => {
        const invs = (invRes.data as unknown as InvoiceBrief[]) || [];
        const invoiced = invs.reduce((s, i) => s + (i.amount || 0), 0);
        const paid = invs
          .filter((i) => i.status === 'paid')
          .reduce((s, i) => s + (i.amount || 0), 0);
        const payouts = (payRes.data as unknown as PayoutBrief[]) || [];
        const payoutsReceived = payouts
          .filter((p) => p.status === 'received' || p.status === 'reconciled')
          .reduce((s, p) => s + (p.net_amount || 0), 0);
        setData({ invoiced, paid, outstanding: invoiced - paid, payoutsReceived });
      })
      .catch((e: unknown) => console.error('[Reports] finance invoices and payouts error:', e));
  }, []);

  return (
    <div className="space-y-6">
      <ReportHeader title="Finance Report" onExport={() => {}} />
      <div className="grid grid-cols-4 gap-4">
        <MetricCard label="Total Invoiced" value={fmtCurrency(data.invoiced)} />
        <MetricCard label="Total Paid" value={fmtCurrency(data.paid)} color="text-success" />
        <MetricCard
          label="Outstanding"
          value={fmtCurrency(data.outstanding)}
          color={data.outstanding > 0 ? 'text-warning' : 'text-ink-bright'}
        />
        <MetricCard
          label="Payouts Received"
          value={fmtCurrency(data.payoutsReceived)}
          color="text-info"
        />
      </div>
    </div>
  );
}

// ============================================================================
// REPORT BUILDER (stub)
// ============================================================================

function ReportBuilder(): JSX.Element {
  const features: Array<{ title: string; desc: string }> = [
    {
      title: 'Select Data Fields',
      desc: 'Choose from any Arbi data table — products, POs, inventory, fulfillment, finance.',
    },
    {
      title: 'Apply Filters',
      desc: 'Filter by date range, channel, distributor, status, and more.',
    },
    {
      title: 'Export Anywhere',
      desc: 'Download as CSV, XLSX, or PDF with formatted headers and currency.',
    },
  ];
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-ink-bright">Report Builder</h2>
        <p className="text-xs text-ink-muted mt-1">
          Build custom reports by selecting data fields, applying filters, and choosing date ranges.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-4">
        {features.map((f) => (
          <div key={f.title} className="bg-surface-1 border border-border rounded-lg p-4">
            <h3 className="text-sm font-medium text-ink-bright mb-2">{f.title}</h3>
            <p className="text-xs text-ink-muted leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </div>
      <div className="text-center">
        <span className="inline-block px-3 py-1 rounded text-xs font-medium bg-accent/10 text-accent">
          Coming in Phase 4
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// ANALYTICS DASHBOARD
// ============================================================================

interface TopProductAnalytics {
  name: string;
  revenue: number;
}

interface AnalyticsOrderLite {
  quantity: number | null;
  unit_price: number | null;
  created_at?: string | null;
  products?: { isg_sku: string | null; title: string | null } | null;
}

interface AnalyticsReturnLite {
  channel: string | null;
}

function AnalyticsDashboard(): JSX.Element {
  const paramStore = useParamStore();
  const warningThreshold = Number(
    paramStore.resolveKey('credit_util_warning_pct')?.param.value ?? 50
  );
  const dangerThreshold = Number(
    paramStore.resolveKey('credit_util_danger_pct')?.param.value ?? 80
  );
  const { activeDistributor } = useApp();
  const [fulfillmentCounts, setFulfillmentCounts] = useState<
    Record<string, number | null | undefined>
  >({});
  const [inventoryCounts, setInventoryCounts] = useState<Record<string, number | null | undefined>>(
    {}
  );
  const [creditSummary, setCreditSummary] = useState<CreditSummaryShape | null>(null);
  const [returnsByChannel, setReturnsByChannel] = useState<NamedValueDatum[]>([]);
  const [topProducts, setTopProducts] = useState<TopProductAnalytics[]>([]);
  const [revenueTrend, setRevenueTrend] = useState<RevenueDatum[]>([]);

  useEffect(() => {
    // Fulfillment pipeline
    callRpc('get_fulfillment_counts')
      .then(({ data }) => {
        if (data && typeof data === 'object')
          setFulfillmentCounts(data as Record<string, number | null | undefined>);
      })
      .then(undefined, (e: unknown) =>
        console.error('[Reports] analytics fulfillment counts error:', e)
      );
    // Inventory
    callRpc('get_inventory_counts')
      .then(({ data }) => {
        if (data && typeof data === 'object')
          setInventoryCounts(data as Record<string, number | null | undefined>);
      })
      .then(undefined, (e: unknown) =>
        console.error('[Reports] analytics inventory counts error:', e)
      );
    // Credit
    if (activeDistributor?.id) {
      callRpc('get_credit_summary', { p_distributor_id: activeDistributor.id })
        .then(({ data }) => {
          if (data) setCreditSummary(data as CreditSummaryShape);
        })
        .then(undefined, (e: unknown) =>
          console.error('[Reports] analytics credit summary error:', e)
        );
    }
    // Returns by channel
    supabase
      .from('returns')
      .select('channel')
      .limit(500)
      .then(
        ({ data: returns }) => {
          const rows = (returns as unknown as AnalyticsReturnLite[]) || [];
          const byChannel: Record<string, number> = {};
          for (const r of rows) {
            const key = r.channel ?? 'unknown';
            byChannel[key] = (byChannel[key] || 0) + 1;
          }
          setReturnsByChannel(Object.entries(byChannel).map(([name, value]) => ({ name, value })));
        },
        (e: unknown) => console.error('[Reports] analytics returns by channel error:', e)
      );
    // Revenue trend (last 30 days)
    const since = new Date(Date.now() - 30 * 86400000).toISOString();
    supabase
      .from('fulfillment_orders')
      .select('quantity, unit_price, created_at')
      .in('status', ['delivered', 'channel_fulfilled'])
      .gte('created_at', since)
      .order('created_at')
      .limit(500)
      .then(
        ({ data: orders }) => {
          const rows = (orders as unknown as AnalyticsOrderLite[]) || [];
          const byDay: Record<string, RevenueDatum> = {};
          for (const o of rows) {
            if (!o.created_at) continue;
            const d = new Date(o.created_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            });
            if (!byDay[d]) byDay[d] = { date: d, revenue: 0 };
            byDay[d].revenue += (o.quantity || 0) * (o.unit_price || 0);
          }
          setRevenueTrend(Object.values(byDay));
        },
        (e: unknown) => console.error('[Reports] analytics revenue trend error:', e)
      );
    // Top 5 products by revenue
    supabase
      .from('fulfillment_orders')
      .select('quantity, unit_price, products(isg_sku, title)')
      .in('status', ['delivered', 'channel_fulfilled'])
      .limit(500)
      .then(
        ({ data: orders }) => {
          const rows = (orders as unknown as AnalyticsOrderLite[]) || [];
          const byProduct: Record<string, TopProductAnalytics> = {};
          for (const o of rows) {
            const sku = o.products?.isg_sku || 'Unknown';
            if (!byProduct[sku]) byProduct[sku] = { name: sku, revenue: 0 };
            byProduct[sku].revenue += (o.quantity || 0) * (o.unit_price || 0);
          }
          setTopProducts(
            Object.values(byProduct)
              .sort((a, b) => b.revenue - a.revenue)
              .slice(0, 5)
          );
        },
        (e: unknown) => console.error('[Reports] analytics top products error:', e)
      );
  }, [activeDistributor]);

  const fulfillmentPipeline: NamedValueDatum[] = Object.entries(fulfillmentCounts)
    .filter(([k]) => !['cancelled'].includes(k))
    .map(([name, value]) => ({ name: name.replace(/_/g, ' '), value: value || 0 }));

  const inventoryPie: NamedValueDatum[] = Object.entries(inventoryCounts)
    .filter(([, v]) => (v || 0) > 0)
    .map(([name, value]) => ({ name: name.replace(/_/g, ' '), value: value || 0 }));

  const utilPct = creditSummary?.utilization_pct || 0;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-ink-bright">Analytics</h2>
      <div className="grid grid-cols-2 gap-4">
        <ChartCard title="Revenue Trend (30d)" empty={revenueTrend.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={revenueTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: 'var(--ink-muted)' }} />
              <YAxis tick={{ fontSize: 9, fill: 'var(--ink-muted)' }} />
              <Tooltip content={<ChartTooltip />} />
              <Line
                type="monotone"
                dataKey="revenue"
                name="Revenue"
                stroke={COLORS.info}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top 5 Products by Revenue" empty={topProducts.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topProducts} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
              <XAxis type="number" tick={{ fontSize: 9, fill: 'var(--ink-muted)' }} />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 9, fill: 'var(--ink-muted)' }}
                width={80}
              />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="revenue" name="Revenue" fill={COLORS.success} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Fulfillment Pipeline" empty={fulfillmentPipeline.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={fulfillmentPipeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 8, fill: 'var(--ink-muted)' }}
                angle={-30}
                textAnchor="end"
                height={50}
              />
              <YAxis tick={{ fontSize: 9, fill: 'var(--ink-muted)' }} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="value" name="Orders" fill={COLORS.accent} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Inventory Health" empty={inventoryPie.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={inventoryPie}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={65}
                innerRadius={35}
                label={({ value }: { value?: number | string }) => `${value ?? ''}`}
              >
                {inventoryPie.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Returns by Channel" empty={returnsByChannel.length === 0}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={returnsByChannel}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: 'var(--ink-muted)' }} />
              <YAxis tick={{ fontSize: 9, fill: 'var(--ink-muted)' }} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="value" name="Returns" fill={COLORS.warning} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Credit Utilization" empty={!creditSummary}>
          <div className="flex flex-col items-center justify-center h-full">
            <div className="relative w-32 h-16 overflow-hidden">
              <svg viewBox="0 0 100 50" className="w-full h-full">
                <path
                  d="M 5 50 A 45 45 0 0 1 95 50"
                  fill="none"
                  stroke="var(--border)"
                  strokeWidth="8"
                  strokeLinecap="round"
                />
                <path
                  d="M 5 50 A 45 45 0 0 1 95 50"
                  fill="none"
                  stroke={
                    utilPct > dangerThreshold
                      ? 'var(--danger)'
                      : utilPct > warningThreshold
                        ? 'var(--warning)'
                        : 'var(--success)'
                  }
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${utilPct * 1.41} 141`}
                />
              </svg>
            </div>
            <p className="text-xl font-mono font-semibold text-ink-bright mt-2">
              {fmtPct(utilPct)}
            </p>
            <p className="text-2xs text-ink-muted">Credit Used</p>
          </div>
        </ChartCard>
      </div>
    </div>
  );
}

// ============================================================================
// LEFT PANEL NAV
// ============================================================================

interface NavItem {
  id: ReportId;
  label: string;
  icon: LucideIcon;
  group: 'reports' | 'analytics';
}

const NAV_ITEMS: readonly NavItem[] = [
  { id: 'profitability', label: 'Profitability', icon: TrendingUp, group: 'reports' },
  { id: 'inventory', label: 'Inventory', icon: Package, group: 'reports' },
  { id: 'purchasing', label: 'Purchasing', icon: ShoppingCart, group: 'reports' },
  { id: 'operations', label: 'Operations', icon: Truck, group: 'reports' },
  { id: 'returns', label: 'Returns', icon: RotateCcw, group: 'reports' },
  { id: 'listings', label: 'Listings', icon: ExternalLink, group: 'reports' },
  { id: 'finance', label: 'Finance', icon: DollarSign, group: 'reports' },
  { id: 'report-builder', label: 'Report Builder', icon: Sliders, group: 'reports' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, group: 'analytics' },
];

// ============================================================================
// MAIN REPORTS PAGE
// ============================================================================

export default function Reports(): JSX.Element {
  const [activeReport, setActiveReport] = useState<ReportId>('profitability');

  const renderContent = (): JSX.Element => {
    switch (activeReport) {
      case 'profitability':
        return <ProfitabilityReport />;
      case 'inventory':
        return <InventoryReport />;
      case 'purchasing':
        return <PurchasingReport />;
      case 'operations':
        return <OperationsReport />;
      case 'returns':
        return <ReturnsReport />;
      case 'listings':
        return <ListingsReport />;
      case 'finance':
        return <FinanceReport />;
      case 'report-builder':
        return <ReportBuilder />;
      case 'analytics':
        return <AnalyticsDashboard />;
      default:
        return <ProfitabilityReport />;
    }
  };

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <h1 className="text-base font-semibold text-ink-bright">Reports</h1>
      </div>

      {/* Two-panel layout */}
      <div className="flex flex-1" style={{ minHeight: 0 }}>
        {/* Left panel */}
        <nav className="w-48 flex-shrink-0 bg-surface-1 border-r border-border overflow-y-auto py-3 px-2">
          <p className="text-2xs uppercase tracking-wider text-ink-muted mb-2 px-2">Reports</p>
          {NAV_ITEMS.filter((i) => i.group === 'reports').map((item) => {
            const Icon = item.icon;
            const active = activeReport === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveReport(item.id)}
                aria-current={active ? 'page' : undefined}
                aria-label={item.label}
                className={clsx(
                  'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-xs transition-colors cursor-pointer mb-0.5',
                  active
                    ? 'bg-accent/10 text-accent border-l-2 border-accent pl-2'
                    : 'text-ink-secondary hover:bg-surface-3 hover:text-ink-primary'
                )}
              >
                <Icon size={14} className="flex-shrink-0" />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
          <div className="border-t border-border-subtle my-3 mx-2" />
          <p className="text-2xs uppercase tracking-wider text-ink-muted mb-2 px-2">Analytics</p>
          {NAV_ITEMS.filter((i) => i.group === 'analytics').map((item) => {
            const Icon = item.icon;
            const active = activeReport === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveReport(item.id)}
                aria-current={active ? 'page' : undefined}
                aria-label={item.label}
                className={clsx(
                  'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-xs transition-colors cursor-pointer mb-0.5',
                  active
                    ? 'bg-accent/10 text-accent border-l-2 border-accent pl-2'
                    : 'text-ink-secondary hover:bg-surface-3 hover:text-ink-primary'
                )}
              >
                <Icon size={14} className="flex-shrink-0" />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Right content */}
        <div className="flex-1 overflow-y-auto p-5">{renderContent()}</div>
      </div>
    </div>
  );
}
